import { zodResolver } from '@hookform/resolvers/zod'
import { signUpAccountSchema, signUpProfileSchema } from '@dyeatlas/shared'
import { ArrowLeft, ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import type { z } from 'zod'
import { AuthLayout, signUpSteps } from './AuthLayout'
import { Button } from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { Checkbox, Notice } from '@/components/ui/misc'
import { authClient, authErrorMessage } from '@/lib/auth-client'

type AccountValues = z.input<typeof signUpAccountSchema>
type ProfileValues = z.input<typeof signUpProfileSchema>

/**
 * Account details are collected first but the account is only created at the end
 * of step 2, so an abandoned sign-up never leaves a half-built user behind and the
 * verification email can carry the researcher's real name.
 */
export function SignUp() {
  const navigate = useNavigate()
  const [step, setStep] = useState<1 | 2>(1)
  const [account, setAccount] = useState<AccountValues | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const accountForm = useForm<AccountValues>({
    resolver: zodResolver(signUpAccountSchema),
    defaultValues: { email: '', password: '', confirmPassword: '', acknowledged: undefined },
  })

  const profileForm = useForm<ProfileValues>({
    resolver: zodResolver(signUpProfileSchema),
    defaultValues: { firstName: '', lastName: '' },
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const submitAccount = accountForm.handleSubmit((values) => {
    setAccount(values)
    setFormError(null)
    setStep(2)
  })

  const submitProfile = profileForm.handleSubmit(async (profile) => {
    if (!account) return setStep(1)
    setFormError(null)

    const { error } = await authClient.signUp.email({
      email: account.email,
      password: account.password,
      name: `${profile.firstName} ${profile.lastName}`,
      firstName: profile.firstName,
      lastName: profile.lastName,
    })

    if (error) {
      setFormError(authErrorMessage(error))
      // A duplicate address is a step 1 problem, so send them back to fix it.
      if (error.code === 'USER_ALREADY_EXISTS') setStep(1)
      return
    }

    navigate(`/verify-email?email=${encodeURIComponent(account.email)}`, { replace: true })
  })

  if (step === 2) {
    return (
      <AuthLayout steps={signUpSteps(2)}>
        <div className="mb-6 text-center">
          <h1 className="font-display text-[26px] font-semibold text-ink">Complete your profile</h1>
          <p className="mt-1 text-[13px] text-ink-muted">Tell us your full name.</p>
        </div>

        <form onSubmit={submitProfile} className="space-y-4">
          {formError && <Notice tone="caution">{formError}</Notice>}

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="First name"
              htmlFor="firstName"
              error={profileForm.formState.errors.firstName?.message}
            >
              <Input
                id="firstName"
                autoComplete="given-name"
                placeholder="Name"
                invalid={Boolean(profileForm.formState.errors.firstName)}
                {...profileForm.register('firstName')}
              />
            </Field>
            <Field
              label="Last name"
              htmlFor="lastName"
              error={profileForm.formState.errors.lastName?.message}
            >
              <Input
                id="lastName"
                autoComplete="family-name"
                placeholder="Last Name"
                invalid={Boolean(profileForm.formState.errors.lastName)}
                {...profileForm.register('lastName')}
              />
            </Field>
          </div>

          <div className="mt-2 h-px bg-line" />

          <div className="flex items-center justify-between">
            <Button type="button" variant="secondary" onClick={() => setStep(1)}>
              <ArrowLeft className="size-4" />
              Back
            </Button>
            <Button type="submit" loading={profileForm.formState.isSubmitting}>
              Continue
              <ArrowRight className="size-4" />
            </Button>
          </div>
        </form>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout steps={signUpSteps(1)}>
      <div className="mb-6 text-center">
        <h1 className="font-display text-[26px] font-semibold text-ink">Create your account</h1>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">
          Use your email address to request access to the DyeAtlas research database.
        </p>
      </div>

      <form onSubmit={submitAccount} className="space-y-4">
        {formError && <Notice tone="caution">{formError}</Notice>}

        <Field label="Email address" htmlFor="email" error={accountForm.formState.errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="name@example.com"
            icon={<Mail className="size-4" />}
            invalid={Boolean(accountForm.formState.errors.email)}
            {...accountForm.register('email')}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          hint="Minimum 10 characters with an uppercase letter and a number or symbol."
          error={accountForm.formState.errors.password?.message}
        >
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a secure password"
              icon={<Lock className="size-4" />}
              className="pr-10"
              invalid={Boolean(accountForm.formState.errors.password)}
              {...accountForm.register('password')}
            />
            <PasswordToggle shown={showPassword} onToggle={() => setShowPassword((v) => !v)} />
          </div>
        </Field>

        <Field
          label="Confirm password"
          htmlFor="confirmPassword"
          error={accountForm.formState.errors.confirmPassword?.message}
        >
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Re-enter your password"
              icon={<Lock className="size-4" />}
              className="pr-10"
              invalid={Boolean(accountForm.formState.errors.confirmPassword)}
              {...accountForm.register('confirmPassword')}
            />
            <PasswordToggle shown={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />
          </div>
        </Field>

        <div>
          <Checkbox
            checked={accountForm.watch('acknowledged') === true}
            onCheckedChange={(checked) =>
              // The field is `z.literal(true)`, so unchecking stores `false` and lets
              // zod produce the "you must acknowledge" message.
              accountForm.setValue('acknowledged', checked as true, {
                shouldValidate: accountForm.formState.isSubmitted,
              })
            }
            label={
              <span className="text-[13px] text-ink-soft">
                I acknowledge the{' '}
                <a href="/terms" className="font-semibold text-brand hover:underline">
                  Terms of Use
                </a>{' '}
                and{' '}
                <a href="/data-policy" className="font-semibold text-brand hover:underline">
                  Research Data Policy
                </a>
                .
              </span>
            }
          />
          {accountForm.formState.errors.acknowledged && (
            <p className="mt-1 text-xs text-brand">
              {accountForm.formState.errors.acknowledged.message}
            </p>
          )}
        </div>

        <Button type="submit" fullWidth>
          Create account
          <ArrowRight className="size-4" />
        </Button>

        <div className="pt-2 text-center text-xs text-ink-muted">
          Already have authorized credentials?{' '}
          <Link to="/sign-in" className="font-semibold text-brand hover:underline">
            Sign in
          </Link>
        </div>
      </form>
    </AuthLayout>
  )
}

function PasswordToggle({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-soft"
      aria-label={shown ? 'Hide password' : 'Show password'}
    >
      {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
    </button>
  )
}
